import classNames from 'classnames'
import {Suspense} from 'react'
import type {EntryPointProps} from 'react-relay'

import SidebarSearch from '../../../../common/SidebarSearch/SidebarSearch'
import SidebarHeader from '../SidebarHeader/SidebarHeader'
import SidebarHeaderShadow from '../SidebarHeaderShadow/SidebarHeaderShadow'
import {useSidebar, useSidebarScroll} from '../SidebarPanelContent.hooks'
import {getSidebarSearchPlaceholder} from '../SidebarPanelContent.utils'
import SidebarPanelContentLoading from '../SidebarPanelContentLoading/SidebarPanelContentLoading'

import type {QueueSidebarQuery} from './__generated__/QueueSidebarQuery.graphql'
import {QueueSidebarSearchContext} from './QueueSidebar.contexts'
import type {ContainerProps, Props} from './QueueSidebar.types'
import QueueSidebarContent from './QueueSidebarContent/QueueSidebarContent'

import styles from './QueueSidebar.css'

type Queries = {main: QueueSidebarQuery}

function QueueSidebar({className, collapsed, width, toggleCollapseSidebar, queryRef}: Props) {
  const [scrollState, onScroll] = useSidebarScroll()
  const {toggleCollapseSidebarAction, ...queueSidebarSearch} = useSidebar(
    collapsed,
    toggleCollapseSidebar,
  )

  const renderSearch = () => (
    <SidebarSearch
      wrapperClassName={styles.searchWrapper}
      placeholder={getSidebarSearchPlaceholder(width, 'Search by pool name')}
      {...queueSidebarSearch}
    />
  )

  return (
    <div className={classNames(className, styles.sidebar)}>
      <QueueSidebarSearchContext.Provider value={queueSidebarSearch}>
        <SidebarHeaderShadow
          sidebarCollapsed={collapsed}
          showShadow={!collapsed && scrollState?.isScrollAtStart === false}
        >
          <SidebarHeader
            className={styles.header}
            sidebarCollapsed={collapsed}
            toggleCollapseSidebar={toggleCollapseSidebarAction}
            search={renderSearch()}
          />
          {!collapsed && <div className={styles.title}>{'POOLS'}</div>}
        </SidebarHeaderShadow>
        {!collapsed && (
          <Suspense fallback={<SidebarPanelContentLoading text="Loading pools..." />}>
            <QueueSidebarContent queryRef={queryRef} onScroll={onScroll} />
          </Suspense>
        )}
      </QueueSidebarSearchContext.Provider>
    </div>
  )
}

export default function QueueSidebarContainer({
  queries,
  props,
}: EntryPointProps<Queries, {}, ContainerProps, {}>) {
  return <QueueSidebar queryRef={queries.main} {...props} />
}

export type QueueSidebarContainerComponent = typeof QueueSidebarContainer
